import { Player } from '@lottiefiles/react-lottie-player';
import { graphql, useStaticQuery } from 'gatsby';
import { not, xor } from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextButton from '@ui/atoms/TextButton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import PhotoUpload from '@ui/organisms/PhotoUpload';

import spaced from '@ui/styled/Spaced';

import useWindowSize from '@hooks/useWindowSize';

import { dropzoneErrors } from '@utils/error';
import { updateLocalUserId } from '@utils/indexedDb';
import updatePhoto from '@utils/updatePhoto';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { SpacingTypes } from '@folx-types/theme';

import Loading from '@assets/lottie/Loading.json';

import theme from '@theme/FolxTheme';

interface IUploadIdStepContent {
	title: string;
	main_content: string;
	upload_id_title: string;
}

export interface IMemberUploadIdStepProps extends IFlowStepProps {
	pageContent: IUploadIdStepContent;
}

const SpacedGridItem = styled(CenteredGridItem)<{
	marginBottom: SpacingTypes;
}>`
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

const SpacedWrapper = styled.div<{
	marginBottom: SpacingTypes;
}>`
	margin-bottom: ${({ marginBottom }) => theme.spacing[marginBottom]};
`;

const StyledPlayer = styled(Player)`
	width: 9rem;
	height: 6rem;
`;

const SpacedNavigationSection = spaced(NavigationSection);

const MemberUploadIdStep: FC<IUploadIdStepProps> = ({ stepKey, pageContent }) => {
	const PHOTO = 'idImage';

	const {
		handleSubmit,
		register,
		setValue,
		formState: { errors, isSubmitted },
	} = useForm();
	const { getAuthToken } = useAuth();
	const { isMobile } = useWindowSize();

	const context = useContext(FlowContext);
	const { handleFormSubmission, handleNavBack, existingFormData } = context;

	const { title, main_content, upload_id_title } = pageContent;

	const [submitting, setSubmitting] = useState(false);
	const [localId, setLocalId] = useState('');
	const [error, setError] = useState('');

	useEffect(() => {
		isSubmitted && !!errors[PHOTO] ? setError(errors[PHOTO]?.message) : setError('');
	}, [isSubmitted, errors[PHOTO]]);

	const onSubmit = async (data) => {
		setSubmitting(true);
		const authorizationToken = await getAuthToken();
		authorizationToken
			? await updatePhoto({
					image: localId,
					imageType: 'Photo ID',
					authorizationToken,
			  })
			: await updateLocalUserId(localId);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<>
			<SpacedGridItem marginBottom="sm">
				<HeadingText>{pageContent.title}</HeadingText>
			</SpacedGridItem>
			<SpacedGridItem marginBottom="lgxl">
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageContent.main_content }} />
			</SpacedGridItem>
			<SpacedGridItem marginBottom="sm">
				<SubheadingText>{pageContent.upload_id_title}</SubheadingText>
			</SpacedGridItem>
			<CenteredGridItem>
				<form onSubmit={handleSubmit(onSubmit)}>
					{error && (
						<SpacedWrapper marginBottom="sm">
							<BasicText color="red" tint="darken40">
								{error}
							</BasicText>
						</SpacedWrapper>
					)}
					<SpacedWrapper marginBottom="xxl">
						<PhotoUpload
							hideDelete
							variant="row"
							previousUpload={localId}
							previousName={existingFormData?.[PHOTO] as string}
							error={!!error}
							imageType="Photo ID"
							handleUpload={(upload, file) => {
								if (not(xor(!!upload, !!file))) {
									setLocalId(upload);
									setValue(PHOTO, file?.name, { shouldValidate: true });
								}
							}}
							handleError={(error) => {
								error
									? setError(dropzoneErrors[error])
									: setError((isSubmitted && !!errors[PHOTO] && errors[PHOTO]?.message) || error);
							}}
							isMobile={isMobile}
						/>
						<input
							type="hidden"
							{...register(PHOTO, {
								required: dropzoneErrors.required,
							})}
						/>
					</SpacedWrapper>
					<SpacedNavigationSection marginBottom="xxxl">
						<Button variant="secondary" onClick={() => handleNavBack(stepKey)} label="Back" />
						<Button
							label={submitting ? <StyledPlayer autoplay loop src={Loading} /> : 'Next'}
							disabled={submitting}
							labelSize="md"
							type="submit"
						/>
					</SpacedNavigationSection>
				</form>
			</CenteredGridItem>
		</>
	);
};

export default MemberUploadIdStep;
